/**
 * Storage Wrapper
 * ===============
 *
 * A wrapper around localStorage that provides the same interface but can be
 * swapped to use in-memory storage for testing.
 *
 * ================================================================================
 * PROBLEM THIS SOLVES
 * ================================================================================
 *
 * In Cypress tests, manipulating localStorage directly is difficult because:
 * 1. Pinia stores load from localStorage before tests can modify it
 * 2. Race conditions between when tests modify storage and when stores load
 * 3. No direct access to Pinia stores via cy.window() (stores not exposed globally)
 * 4. Vue reactivity doesn't trigger correctly when modifying localStorage via cy.window()
 *
 * ================================================================================
 * SOLUTION
 * ================================================================================
 *
 * This wrapper provides two modes:
 *
 * PRODUCTION MODE (default):
 * - Delegates all calls directly to localStorage
 * - No performance overhead
 * - Transparent to the application
 *
 * TEST MODE (enabled via window flag):
 * - Uses in-memory Map storage instead of localStorage
 * - Allows tests to pre-populate storage before stores load
 * - Allows tests to directly inspect storage state
 * - Avoids timing/reactivity issues
 *
 * ================================================================================
 * USAGE
 * ================================================================================
 *
 * In production code (no changes needed):
 *   import { storageWrapper } from '@/utils/storageWrapper'
 *   const data = storageWrapper.getItem('key')
 *   storageWrapper.setItem('key', 'value')
 *
 * In Cypress tests:
 *
 *   // Option 1: Enable via window flag (before app loads)
 *   cy.visit('/', {
 *     onBeforeLoad: (win) => {
 *       win.__CYPRESS_STORAGE_TEST_MODE__ = true
 *       // Pre-populate test storage
 *       win.localStorage.setItem('eams_recently_viewed', JSON.stringify([...]))
 *     }
 *   })
 *
 *   // Option 2: Use helper command
 *   cy.loginWithTestStorage('user@example.com', {
 *     storageData: {
 *       'eams_recently_viewed': JSON.stringify([...])
 *     }
 *   })
 *
 * ================================================================================
 * IMPLEMENTATION
 * ================================================================================
 *
 * The wrapper checks for the window flag __CYPRESS_STORAGE_TEST_MODE__ during
 * construction (before any storage operations happen). This flag must be set
 * BEFORE the app loads (in onBeforeLoad), not after.
 *
 * Methods:
 * - getItem(key): Get value from storage
 * - setItem(key, value): Set value in storage
 * - removeItem(key): Remove value from storage
 * - clear(): Clear all storage
 * - length: Number of items in storage
 * - keys(): Get all storage keys (for debugging)
 * - enableTestMode(): Manually enable test mode
 * - disableTestMode(): Manually disable test mode
 * - clearTestStorage(): Clear test storage only
 * - isTestMode(): Check if currently in test mode
 *
 * ================================================================================
 * FILES USING THIS WRAPPER
 * ================================================================================
 *
 * - src/stores/recentlyViewedStore.js
 *   - Recently viewed accommodations (cross-tab sync)
 *
 * - src/services/enumCacheService.js
 *   - Enum data cache with 1-hour TTL (cross-tab sync)
 *
 * - src/services/amenityCacheService.js
 *   - Amenity data cache with 1-hour TTL (cross-tab sync)
 *
 * ================================================================================
 * CREATED FOR
 * ================================================================================
 *
 * This wrapper was created to support testing the "Action Required" section
 * (AccommodationIndex.vue), which depends on recentlyViewedStore.pendingItems.
 *
 * The storageWrapper allows tests to pre-populate the recentlyViewedStore with
 * pending items before the AccommodationIndex component loads, avoiding the
 * complex timing issues that arise when trying to modify localStorage after
 * Pinia stores have already initialized.
 *
 * NOTE: As of 2025-01-26, the storageWrapper is correctly implemented and
 * solves the storage pre-population problem. However, the "Action Required"
 * tests still fail due to a separate issue: userProfile is undefined after
 * cy.loginAs(), which prevents showingInitialFavorites from being set to true.
 *
 * See cypress/e2e/action-required.cy.js for full investigation details.
 */

class StorageWrapper {
  constructor() {
    // Check for test mode flag on window (for Cypress tests)
    // This must be checked BEFORE any localStorage calls happen
    if (typeof window !== 'undefined' && window.__CYPRESS_STORAGE_TEST_MODE__) {
      this._testMode = true
      this._testStorage = new Map() // In-memory storage for tests

      // Copy any existing localStorage data into test storage
      // This allows tests to pre-populate data via localStorage before stores load
      if (typeof window.localStorage !== 'undefined') {
        for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i)
          if (key) {
            const value = window.localStorage.getItem(key)
            this._testStorage.set(key, value)
          }
        }
        console.log('[StorageWrapper] Test mode enabled, copied', this._testStorage.size, 'items from localStorage')
      }
    } else {
      this._testMode = false
      this._testStorage = new Map() // In-memory storage for tests
    }
  }

  /**
   * Enable test mode - uses in-memory Map instead of localStorage
   * Call this before importing/using stores in tests
   */
  enableTestMode() {
    this._testMode = true
    this._testStorage.clear()
    console.log('[StorageWrapper] Test mode enabled')
  }

  /**
   * Disable test mode - returns to using actual localStorage
   */
  disableTestMode() {
    this._testMode = false
    this._testStorage.clear()
    console.log('[StorageWrapper] Test mode disabled')
  }

  /**
   * Clear test storage
   * Useful for cleaning up between tests
   */
  clearTestStorage() {
    this._testStorage.clear()
    console.log('[StorageWrapper] Test storage cleared')
  }

  /**
   * Get an item from storage
   * @param {string} key - The key to retrieve
   * @returns {string|null} The value or null if not found
   */
  getItem(key) {
    if (this._testMode) {
      return this._testStorage.get(key) ?? null
    }
    return localStorage.getItem(key)
  }

  /**
   * Set an item in storage
   * @param {string} key - The key to set
   * @param {string} value - The value to store
   */
  setItem(key, value) {
    if (this._testMode) {
      this._testStorage.set(key, value)
      return
    }
    localStorage.setItem(key, value)
  }

  /**
   * Remove an item from storage
   * @param {string} key - The key to remove
   */
  removeItem(key) {
    if (this._testMode) {
      this._testStorage.delete(key)
      return
    }
    localStorage.removeItem(key)
  }

  /**
   * Clear all items from storage
   */
  clear() {
    if (this._testMode) {
      this._testStorage.clear()
      return
    }
    localStorage.clear()
  }

  /**
   * Get the number of items in storage
   * @returns {number} The count of items
   */
  get length() {
    if (this._testMode) {
      return this._testStorage.size
    }
    return localStorage.length
  }

  /**
   * Get all keys in storage (for debugging/tests)
   * @returns {string[]} Array of keys
   */
  keys() {
    if (this._testMode) {
      return Array.from(this._testStorage.keys())
    }
    return Object.keys(localStorage)
  }

  /**
   * Check if currently in test mode
   * @returns {boolean} True if test mode is enabled
   */
  isTestMode() {
    return this._testMode
  }
}

// Export singleton instance
export const storageWrapper = new StorageWrapper()

// Also export the class for testing
export { StorageWrapper }
